import React, { useMemo, useCallback } from 'react';
import {
  useParams, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import { useGetFilteredTopicQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale, UNGATED_CLIENT_ID } from 'src/contexts/ClientContext';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { TopicPageHeader, ContentBanner } from '@amzn/seller-university-ui-components';
import LearningPathwayBannersSection from 'src/components/learning-pathway/LearningPathwayBannersSection';
import { LearningModuleModal } from 'src/components/learning-module';
import {
  EntityType, LISTING_TOPIC_IDS, BESSIE_IMAGE_URL, BESSIE_IMAGE_ALT,
  BESSIE_SUPPORTED_LOCALES, BESSIE_SUPPORTED_MARKETPLACE,
} from 'src/constants/entity';
import { ROUTE_SEARCH_PARAM, ROUTES } from 'src/constants/route';
import useScrollToElement from 'src/hooks/useScrollToElement';
import { removeSearchParam } from 'src/utils/searchParams';
import { BUNDLE_NAME, TOPIC_STRING_ID } from 'src/constants/localization';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { REF_TAG } from 'src/constants/refTag';
import { getStatusCodeFromError } from 'src/utils/error';
import SubtopicsSection from './SubtopicsSection';

const TopicPage = () => {
  const { topicId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const learningModuleId = searchParams.get(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID);
  const [topicBundle] = useBundle(BUNDLE_NAME.TOPIC);
  const currentLocale = document.documentElement.lang.toLowerCase();
  const clientId = useClientId();
  const selLocale = useSelLocale();

  if (!topicId) {
    const errorMessage = `Expected topicId to be present in browser URL path: ${location.pathname}`;
    const error = new Error(errorMessage);
    ClientLogger.error(ClientLog.IllegalInternalDataState, error, {
      path: location.pathname,
      url: window.location.href,
    });
    throw error;
  }

  // Only use the scroll hook for T1 treatment
  const { registerElement, scrollToElement } = useScrollToElement();

  const {
    data: topicData, isLoading: topicLoading, isError: topicError, error: topicErrorObj,
  } = useGetFilteredTopicQuery({ topicId, clientId, selLocale });

  // Create buttons from subtopic list when using filter API - only show for T1 treatment
  const subtopicButtons = useMemo(() => {
    if (!topicData?.topic.subTopicList) {
      return undefined;
    }

    return topicData.topic.subTopicList.map((subtopic) => ({
      label: subtopic.title,
      onClick: () => {
        // Use React-based scrolling for T1 treatment
        if (scrollToElement) {
          scrollToElement(`subtopic-${subtopic.subtopicId}`);
        }
      },
    }));
  }, [topicData?.topic.subTopicList, scrollToElement]);

  const bessieBanner = useMemo(() => (
    <ContentBanner
      title={t(topicBundle, TOPIC_STRING_ID.INTERACTIVE_BANNER_TITLE)}
      description={t(topicBundle, TOPIC_STRING_ID.INTERACTIVE_BANNER_DESCRIPTION)}
      imageSrc={BESSIE_IMAGE_URL}
      imageAlt={BESSIE_IMAGE_ALT}
      imagePosition="left"
      actionButton={{
        label: t(topicBundle, TOPIC_STRING_ID.INTERACTIVE_BANNER_BUTTON),
        variant: 'primary',
        linkProps: {
          href: ROUTES.INTERACTIVE_LEARNING,
          refTag: REF_TAG.TOPIC_BESSIE_BANNER,
        },
      }}
    />
  ), [topicBundle]);

  const removeModuleSearchParam = useCallback(() => {
    navigate({ search: removeSearchParam(searchParams, ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID) }, { replace: true });
  }, [navigate, searchParams]);

  if (topicLoading) return <LoadingView />;

  if (topicError) {
    ClientLogger.error(ClientLog.IllegalInternalDataState, {
      topicId,
      path: location.pathname,
      url: window.location.href,
    });
    return <ErrorView statusCode={getStatusCodeFromError(topicErrorObj)} />;
  }

  return (
    <div className="flex flex-col px-su-space-08">
      {topicData && (
        <section>
          <TopicPageHeader
            title={topicData.topic.title}
            description={topicData.topic.description}
            thumbnailLink={topicData.topic.thumbnailLink}
            {...(subtopicButtons && { buttons: subtopicButtons })}
          />
        </section>
      )}

      {topicData?.topic.learningPathways && topicData.topic.learningPathways.length > 0 && (
        <section className="mb-su-space-08">
          <LearningPathwayBannersSection learningPathwayIds={topicData.topic.learningPathways} />
        </section>
      )}

      {LISTING_TOPIC_IDS.includes(topicId)
        && BESSIE_SUPPORTED_LOCALES.includes(currentLocale)
        && topicData?.topic?.marketplaceId === BESSIE_SUPPORTED_MARKETPLACE
        && clientId !== UNGATED_CLIENT_ID && (
        <section className="mb-su-space-08">
          {bessieBanner}
        </section>
      )}

      {topicData?.topic.subTopicList && (
        <SubtopicsSection
          topicId={topicId}
          subTopicList={topicData.topic.subTopicList}
          registerElement={registerElement}
        />
      )}

      {learningModuleId && (
        <LearningModuleModal
          learningModuleId={learningModuleId}
          onModalClose={removeModuleSearchParam}
          parentId={topicId}
          parentType={EntityType.Topic}
        />
      )}
    </div>
  );
};

export default TopicPage;
